import React, { useMemo, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import {
  Modal,
  Button,
  PublishedIcon,
} from "@/components";
import { updateModal } from "@/store/app/app.slice";
import {
  formatTimestampToAgo,
  generateThumbnailUrl,
} from "@/utils";
import FailureThumb from "@/assets/icons/svg/failure.svg";
import styles from "./ArticlePreviewModal.module.scss";

interface ArticlePreviewModalProps {
  isOpen: boolean;
  article: any | null;
  articleDetails: any | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onSetAsLead?: () => void;
  onSetAsEmbed?: () => void;
  onAddDenmarkEmbedChip?: (article: any, type: "lead" | "embed") => void;
  hostid?: string;
  openedFromInlineSearch?: boolean;
  inlineSearchQuery?: string;
  onBackToSearch?: (query: string) => void;
}
const createElement = (
  doc: Document,
  tag: string,
  className?: string,
  textContent?: string
) => {
  const element = doc.createElement(tag);
  if (className) element.className = className;
  if (textContent) element.textContent = textContent;
  return element;
};

const createEmbedCard = (
  doc: Document,
  {
    msid,
    embedType,
    title,
    thumbUrl,
    embedId,
    cmsId,
  }: {
    msid: string | null;
    embedType: string;
    title: string;
    thumbUrl?: string;
    embedId?: string | null;
    cmsId?: string | null;
  }
) => {
  const innerCard = createElement(doc, "div", styles.embedCard);

  const displayId = embedId || cmsId || msid;
  const idBoxContent = createElement(doc, "div", styles.idBoxContent);
  const textContent = createElement(
    doc,
    "span",
    styles.idBoxText,
    displayId ? "ID:" : ""
  );
  const idBox = createElement(
    doc,
    "span",
    styles.idBox,
    displayId ? `${displayId}` : embedType.toUpperCase()
  );

  const profileSection = createElement(
    doc,
    "div",
    `${styles.profileSection} ${
      embedType === "video" && thumbUrl ? styles.videoBackground : ""
    }`
  );

  if (embedType === "video" && thumbUrl) {
    profileSection.style.backgroundImage = `url(${thumbUrl})`;
  }

  if (embedType !== "video") {
    const avatar = createElement(doc, "div", styles.avatar, "👤");
    const profileInfo = createElement(doc, "div", styles.profileInfo);
    const profileName = createElement(
      doc,
      "div",
      styles.profileName,
      title || "User"
    );
    const profileHandle = createElement(
      doc,
      "div",
      styles.profileHandle,
      `@${title?.toLowerCase().replace(/\s+/g, "_") || "user"}`
    );

    profileInfo.appendChild(profileName);
    profileInfo.appendChild(profileHandle);
    profileSection.appendChild(avatar);
    profileSection.appendChild(profileInfo);
  }

  if (embedType === "video" && thumbUrl) {
    const playOverlay = createElement(doc, "div", styles.playOverlay);
    const playIcon = doc.createElement("i");
    playIcon.className = `bi bi-play-fill text-white ${styles.playIcon}`;
    playOverlay.appendChild(playIcon);
    profileSection.appendChild(playOverlay);
  }

  // Content container (right side)
  const contentContainer = createElement(
    doc,
    "div",
    displayId ? styles.contentContainer : styles.contentContainerWithoutId
  );
  const contentText = createElement(
    doc,
    "div",
    styles.contentText,
    `${title || "User"} | Can't fetch the ${embedType} data`
  );

  if (displayId) {
    contentContainer.appendChild(idBoxContent);
  }
  idBoxContent.appendChild(textContent);
  idBoxContent.appendChild(idBox);
  contentContainer.appendChild(contentText);

  innerCard.appendChild(profileSection);
  innerCard.appendChild(contentContainer);

  // Add icon container at bottom right (append last so it appears on top)
  const iconContainer = createElement(doc, "div", styles.iconContainer);
  iconContainer.setAttribute("data-published-icon", "true");
  iconContainer.setAttribute("data-embed-type", embedType);
  innerCard.appendChild(iconContainer);

  return innerCard;
};

const ArticlePreviewModal: React.FC<ArticlePreviewModalProps> = ({
  isOpen,
  article,
  articleDetails,
  loading,
  error,
  onClose,
  onSetAsLead,
  onSetAsEmbed,
  onAddDenmarkEmbedChip,
  hostid,
  openedFromInlineSearch = false,
  inlineSearchQuery = "",
  onBackToSearch,
}) => {
  const handleSetAsType = async (type: "lead" | "embed") => {
    if (article) {
      const articleForChip = {
        ...article,
        selectedHost: hostid,
      };
      onAddDenmarkEmbedChip?.(articleForChip, type);
    }

    if (type === "lead") {
      onSetAsLead?.();
    } else {
      onSetAsEmbed?.();
    }
  };

  const renderStoryContent = useMemo(() => {
    if (!articleDetails?.story) {
      return null;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(articleDetails.story, "text/html");

      const cmsembedElements = Array.from(doc.querySelectorAll("cmsembed"));
      cmsembedElements.forEach((embed, index) => {
        const type = embed.getAttribute("type");
        const msid = embed.getAttribute("msid");
        const embedId = embed.getAttribute("id");
        const embedHostId = embed.getAttribute("hostid");
        const title = embed.getAttribute("title") || "";
        const thumbUrl =
          embed.getAttribute("thumbUrl") ||
          embed.getAttribute("thumburl") ||
          "";
        const embedType = type?.toLowerCase() || "unknown";

        const container = doc.createElement("div");
        container.className = styles.embedContainer;
        container.setAttribute("data-cmsembed", "true");
        container.setAttribute("data-embed-id", `embed-${index}`);
        container.setAttribute("data-embed-type", embedType);
        container.setAttribute("data-embed-index", String(index));

        if (embedType === "image" && msid) {
          // Handle image embeds - keep as is
          const imageWrapper = doc.createElement("div");
          imageWrapper.style.position = "relative";
          imageWrapper.style.display = "inline-block";

          const image = doc.createElement("img");
          image.className = styles.embedImage;
          image.src =
            thumbUrl ||
            generateThumbnailUrl(
              msid,
              Number(embedHostId || hostid || article?.selectedHost)
            );
          image.alt = title;
          image.addEventListener("error", () => {
            image.src = FailureThumb;
          });

          const caption = doc.createElement("span");
          caption.className = styles.embedCaption;
          caption.textContent = title || "Embedded Image";

          // Add icon container at bottom right for images
          const iconContainer = createElement(doc, "div", styles.iconContainer);
          iconContainer.setAttribute("data-published-icon", "true");
          iconContainer.setAttribute("data-embed-type", embedType);
          imageWrapper.appendChild(iconContainer);

          imageWrapper.appendChild(image);
          container.appendChild(imageWrapper);
          container.appendChild(caption);
        } else {
          const thumbnailUrl =
            embedType === "video" && msid
              ? thumbUrl ||
                generateThumbnailUrl(
                  msid,
                  Number(embedHostId || hostid || article?.selectedHost)
                )
              : undefined;

          const innerCard = createEmbedCard(doc, {
            msid,
            embedType,
            title,
            thumbUrl: thumbnailUrl,
            embedId,
            cmsId: embed.getAttribute("cmsId") || "",
          });

          container.className = `${styles.embedContainer} w-100 d-flex align-items-start`;
          container.appendChild(innerCard);
        }

        embed.replaceWith(container);
      });

      return doc.body.innerHTML;
    } catch (error) {
      console.error("Failed to parse article story HTML", error);
      return articleDetails.story;
    }
  }, [articleDetails?.story, article?.selectedHost, hostid]);

  const storyContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!storyContentRef.current || !renderStoryContent) return;

    // Find all icon containers (both in embed cards and image wrappers)
    const iconContainers = storyContentRef.current.querySelectorAll(
      "[data-published-icon]"
    );
    const roots: Array<{
      container: Element;
      root: ReturnType<typeof createRoot>;
    }> = [];

    iconContainers.forEach((iconContainer) => {
      // Skip if already rendered
      if (iconContainer.querySelector("i, img")) return;

      const embedType = iconContainer.getAttribute("data-embed-type");
      if (!embedType) return;

      const root = createRoot(iconContainer);
      root.render(
        <PublishedIcon
          job_type={embedType.includes("instagram") ? "instagram" : embedType}
          status={10}
          size={16}
          showQuestionCircle={false}
        />
      );
      roots.push({ container: iconContainer, root });
    });

    return () => {
      roots.forEach(({ root }) => {
        try {
          root.unmount();
        } catch (error) {
          // Ignore unmount errors
        }
      });
    };
  }, [renderStoryContent]);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Article Preview"
      width="90vw"
      closeOnOutsideClick={true}
      updateModal={updateModal}
      height="100%"
      footer={
        <div className="d-flex justify-content-between gap-4 w-100">
          <Button
            type="outline-primary"
            onClick={() => {
              if (
                openedFromInlineSearch &&
                inlineSearchQuery &&
                onBackToSearch
              ) {
                onBackToSearch(inlineSearchQuery);
              } else {
                onClose();
              }
            }}
          >
            Back
          </Button>
          <div className="d-flex flex-row align-items-center gap-2">
            <Button
              type="primary"
              onClick={() => handleSetAsType("lead")}
              isDisabled={articleDetails?.isVideoEligible === false}
              title={
                articleDetails?.isVideoEligible === false
                  ? "This article is not eligible for video"
                  : ""
              }
            >
              Set as Lead
            </Button>
          </div>
        </div>
      }
    >
      <div className="px-2 article-preview">
        {loading && (
          <div className="text-center py-4 position-absolute top-50 start-50 translate-middle">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-2">Loading article details...</div>
          </div>
        )}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
          </div>
        )}
        {!loading && !error && (
          <>
            <h5 className="fw-semibold mb-2">
              {articleDetails?.name || "No Title Available"}
            </h5>
            <>
              <div className="d-flex gap-1 H">
                {(article?.selectedTypes?.includes("lead") ||
                  article?.selectedType === "lead") && (
                  <span className={`badge bg-primary ${styles.badgeSmall}`}>
                    LEAD
                  </span>
                )}
                {(article?.selectedTypes?.includes("embed") ||
                  article?.selectedType === "embed") && (
                  <span className={`badge bg-success ${styles.badgeSmall}`}>
                    EMBED
                  </span>
                )}
              </div>
              <div className="d-flex gap-2 text-muted ">
                {articleDetails?.artInsertedBy && (
                  <span>Created by: {articleDetails.artInsertedBy}</span>
                )}
                {articleDetails?.artInsertedBy &&
                  articleDetails?.artUpdDate && <span>|</span>}
                {articleDetails?.artUpdDate && (
                  <span>
                    Uploaded on:{" "}
                    {formatTimestampToAgo(Number(articleDetails.artUpdDate))}
                  </span>
                )}
              </div>
            </>
            {articleDetails?.seopath && (
              <p className="text-muted my-2 font-italic">
                <b>SEO Path:</b> {articleDetails.seopath}
              </p>
            )}
            {articleDetails?.seoTitle && (
              <div className="mb-3">
                <p className="mb-0 lh-18 text-secondary font-italic">
                  {articleDetails.subject}
                </p>{" "}
              </div>
            )}
            {articleDetails?.story && (
              <div className="mb-3">
                {article.msid && (
                  <img
                    src={generateThumbnailUrl(article.msid, Number(hostid))}
                    alt="Article Thumbnail"
                    onError={(e) => {
                      e.currentTarget.src = FailureThumb;
                    }}
                    className={`img-fluid ${styles.articleThumbnail}`}
                  />
                )}
                <div
                  ref={storyContentRef}
                  className="mb-0"
                  dangerouslySetInnerHTML={{
                    __html: renderStoryContent ?? articleDetails.story,
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default ArticlePreviewModal;
